import { ReactNode, useCallback, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { CreditCard } from '@mui/icons-material';

import { LegacyFeatureFlag } from '@/features/feature-flags';
import { NotificationProps } from '@/features/notifications/types/NotificationProps';
import { useSyncKoObservable } from '@/hooks/useSyncKoObservable';
import { CompletionStagesBackend } from '@/legacy/features/companies/creditCards/components/helper';
import { Instance as CompanyService } from '@/legacy/services/company';
import { IsFeatureEnabled, IsFeatureEnabledForCompanyProfile } from '@/legacy/utils/plooto';
import CompanyContext from '@/providers/CompanyContext';
import { durandalRoutes } from '@/routes/durandal';
import readJsonFromLocalStorage from '@/utils/readJsonFromLocalStorage';
import writeJsonToLocalStorage from '@/utils/writeJsonToLocalStorage';

const title = <FormattedMessage defaultMessage="Get paid by credit card" />;
const descriptionByStatus: Record<CompletionStagesBackend, ReactNode> = {
  [CompletionStagesBackend.Approved]: (
    <FormattedMessage defaultMessage="Your credit card application was approved." />
  ),
  [CompletionStagesBackend.Reviewing]: (
    <FormattedMessage defaultMessage="Your credit card application is under review. Check back soon for updates." />
  ),
  [CompletionStagesBackend.Completed]: (
    <FormattedMessage defaultMessage="Your credit card application is under review. Check back soon for updates." />
  ),
  [CompletionStagesBackend.Rejected]: (
    <FormattedMessage defaultMessage="Your credit card application was rejected." />
  ),
};
const descriptionDefault = (
  <FormattedMessage defaultMessage="Get paid faster by enabling credit cards for your receivables." />
);

export const useCreditCardReceivablesNotification = () => {
  const { companyId } = useContext(CompanyContext);
  const profile = useSyncKoObservable(CompanyService.profile);
  const membership = useSyncKoObservable(CompanyService.membership);
  const isLimitedPaymentsCapable = useSyncKoObservable(
    CompanyService.hasApprovedLimitedDomesticPaymentsCapability
  );
  const isPaymentsCapable = useSyncKoObservable(membership?.isPaymentsCapable);
  const isAvailable = useMemo(
    () =>
      !isLimitedPaymentsCapable &&
      isPaymentsCapable &&
      (IsFeatureEnabled(LegacyFeatureFlag.FEATURE_PAYMENT_REQUESTS_CREDIT_CARDS, companyId) ||
        IsFeatureEnabledForCompanyProfile(
          profile,
          LegacyFeatureFlag.FEATURE_PAYMENT_REQUESTS_CREDIT_CARDS
        )),
    [companyId, isLimitedPaymentsCapable, isPaymentsCapable, profile]
  );
  const creditCardPaymentsCapabilityStatus = useSyncKoObservable(
    profile?.creditCardPaymentsCapabilityStatus
  );
  const localStorageKey = `isCreditCardBannerDismissed_${companyId}`;
  const isUnread =
    creditCardPaymentsCapabilityStatus !== CompletionStagesBackend.Approved &&
    creditCardPaymentsCapabilityStatus !== CompletionStagesBackend.Completed &&
    creditCardPaymentsCapabilityStatus !== CompletionStagesBackend.Rejected &&
    readJsonFromLocalStorage<boolean>(localStorageKey, false) !== true;
  const markAsRead = useCallback(() => {
    writeJsonToLocalStorage(localStorageKey, true);
  }, [localStorageKey]);
  const notification: NotificationProps = {
    key: 'CreditCardReceivables',
    isLoading: false,
    isUnread,
    href: durandalRoutes.user.integrations.creditCards,
    target: '_self',
    markAsRead,
    Icon: CreditCard,
    title,
    description: descriptionByStatus[creditCardPaymentsCapabilityStatus] ?? descriptionDefault,
  };
  return isAvailable ? notification : null;
};
